import React, { useState, useEffect } from "react";
import { Clock, Bell, Play, Database, Download, Trash2, RefreshCw, HardDrive, Timer } from "lucide-react";
import * as api from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";
import { Label } from "../ui/label";
import { PageLoader } from "../shared/LoadingSpinner";
import { toast } from "sonner";

function BackupSection() {
    const [backups, setBackups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [triggering, setTriggering] = useState(false);

    useEffect(() => { loadBackups(); }, []);

    const loadBackups = async () => {
        try {
            setLoading(true);
            const data = await api.listBackups();
            setBackups(data);
        } catch { toast.error("Erreur chargement sauvegardes"); }
        finally { setLoading(false); }
    };

    const handleTrigger = async () => {
        try {
            setTriggering(true);
            const result = await api.triggerBackup();
            toast.success(`Sauvegarde créée : ${result.name}`);
            loadBackups();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erreur lors de la sauvegarde");
        }
        finally { setTriggering(false); }
    };

    const handleDelete = async (name) => {
        if (!window.confirm(`Supprimer définitivement la sauvegarde ${name} ?`)) return;
        try {
            await api.deleteBackup(name);
            toast.success("Sauvegarde supprimée");
            loadBackups();
        } catch { toast.error("Erreur"); }
    };

    const formatSize = (bytes) => {
        if (!bytes) return "—";
        if (bytes < 1024) return `${bytes} o`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
    };

    const formatDate = (iso) => {
        if (!iso) return "—";
        const d = new Date(iso);
        return d.toLocaleString("fr-CH", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    };

    return (
        <div className="card" data-testid="backup-section">
            <div className="card-header flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Database className="w-4 h-4 text-gray-500" />
                    <h2 className="text-sm font-semibold">Sauvegardes MongoDB</h2>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={loadBackups} disabled={loading} data-testid="refresh-backups-btn">
                        <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
                    </Button>
                    <Button size="sm" onClick={handleTrigger} disabled={triggering} data-testid="trigger-backup-btn">
                        <HardDrive className="w-3.5 h-3.5 mr-1" />
                        {triggering ? "En cours..." : "Sauvegarder maintenant"}
                    </Button>
                </div>
            </div>
            <div className="card-body">
                <p className="text-sm text-gray-500 mb-3">
                    Les sauvegardes automatiques sont créées quotidiennement à 2h00 UTC. Seules les 7 dernières sont conservées.
                </p>
                {loading ? (
                    <div className="text-sm text-gray-400 py-4 text-center">Chargement...</div>
                ) : backups.length === 0 ? (
                    <div className="text-sm text-gray-400 py-4 text-center">Aucune sauvegarde disponible</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="backups-table">
                            <thead>
                                <tr className="border-b text-left text-gray-500 text-xs uppercase tracking-wide">
                                    <th className="pb-2 pr-4">Nom</th>
                                    <th className="pb-2 pr-4">Date</th>
                                    <th className="pb-2 pr-4">Taille</th>
                                    <th className="pb-2 pr-4">Type</th>
                                    <th className="pb-2 pr-4">Statut</th>
                                    <th className="pb-2 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {backups.map((b) => (
                                    <tr key={b.name} className="border-b border-gray-100 last:border-0" data-testid={`backup-row-${b.name}`}>
                                        <td className="py-2 pr-4 font-mono text-xs">{b.name}</td>
                                        <td className="py-2 pr-4 text-gray-600">{formatDate(b.created_at)}</td>
                                        <td className="py-2 pr-4 text-gray-600">{formatSize(b.size)}</td>
                                        <td className="py-2 pr-4">
                                            <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${b.type === "scheduled" ? "bg-blue-100 text-blue-700" : "bg-green-100 text-green-700"}`}>
                                                {b.type === "scheduled" ? "Auto" : "Manuel"}
                                            </span>
                                        </td>
                                        <td className="py-2 pr-4">
                                            <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${b.file_exists ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                                                {b.file_exists ? "OK" : "Fichier manquant"}
                                            </span>
                                        </td>
                                        <td className="py-2 text-right">
                                            <div className="flex items-center justify-end gap-1">
                                                {b.file_exists && (
                                                    <a
                                                        href={api.downloadBackupUrl(b.name)}
                                                        className="inline-flex items-center justify-center w-7 h-7 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition"
                                                        title="Télécharger"
                                                        data-testid={`download-backup-${b.name}`}
                                                    >
                                                        <Download className="w-3.5 h-3.5" />
                                                    </a>
                                                )}
                                                <button
                                                    onClick={() => handleDelete(b.name)}
                                                    className="inline-flex items-center justify-center w-7 h-7 rounded hover:bg-red-50 text-gray-400 hover:text-red-600 transition"
                                                    title="Supprimer"
                                                    data-testid={`delete-backup-${b.name}`}
                                                >
                                                    <Trash2 className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

export default function AdminSettings() {
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [autoCloseDays, setAutoCloseDays] = useState(7);
    const [slaAutoEnabled, setSlaAutoEnabled] = useState(true);
    const [slaHours, setSlaHours] = useState({ low: 72, medium: 24, high: 8, critical: 4 });
    const [saving, setSaving] = useState(false);
    const [running, setRunning] = useState(false);

    useEffect(() => { loadSettings(); }, []);

    const loadSettings = async () => {
        try {
            setLoading(true);
            const data = await api.getSystemSettings();
            setSettings(data);
            setAutoCloseDays(data.auto_close_days ?? 7);
            setSlaAutoEnabled(data.sla_auto_enabled ?? true);
            setSlaHours(data.sla_resolution_hours ?? { low: 72, medium: 24, high: 8, critical: 4 });
        } catch { toast.error("Erreur chargement paramètres"); }
        finally { setLoading(false); }
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            await api.updateSystemSettings({
                auto_close_days: parseInt(autoCloseDays) || 7,
                sla_auto_enabled: slaAutoEnabled,
                sla_resolution_hours: slaHours,
            });
            toast.success("Paramètres enregistrés");
        } catch { toast.error("Erreur"); }
        finally { setSaving(false); }
    };

    const handleAutoClose = async () => {
        try {
            setRunning(true);
            const result = await api.triggerAutoClose();
            toast.success(result.message);
        } catch { toast.error("Erreur"); }
        finally { setRunning(false); }
    };

    const handleReminders = async () => {
        try {
            setRunning(true);
            const result = await api.triggerReminders();
            toast.success(result.message);
        } catch { toast.error("Erreur"); }
        finally { setRunning(false); }
    };

    if (loading) return <PageLoader />;

    return (
        <div className="page-container" data-testid="admin-settings">
            <div className="page-header">
                <h1 className="page-title">Paramètres système</h1>
                <p className="page-subtitle">Configuration de la clôture automatique, des relances et des sauvegardes</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* SLA / Deadline section */}
                <div className="card md:col-span-2">
                    <div className="card-header flex items-center gap-2">
                        <Timer className="w-4 h-4 text-gray-500" />
                        <h2 className="text-sm font-semibold">Dates limites et SLA</h2>
                    </div>
                    <div className="card-body space-y-5">
                        <div className="flex items-center gap-3">
                            <Switch
                                checked={slaAutoEnabled}
                                onCheckedChange={setSlaAutoEnabled}
                                data-testid="sla-auto-toggle"
                            />
                            <Label className="text-sm font-medium cursor-pointer">
                                Deadline automatique si non définie par l'utilisateur
                            </Label>
                        </div>
                        <p className="text-sm text-gray-500">
                            {slaAutoEnabled
                                ? "Quand un utilisateur crée un ticket sans date limite, le système en calcule une automatiquement selon la priorité."
                                : "Les tickets sans date limite manuelle n'auront pas de deadline."}
                        </p>

                        {slaAutoEnabled && (
                            <div className="border rounded-lg overflow-hidden">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="bg-gray-50 border-b">
                                            <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">Priorité</th>
                                            <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">Délai de résolution</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {[
                                            { key: "low", label: "Basse" },
                                            { key: "medium", label: "Moyenne" },
                                            { key: "high", label: "Haute" },
                                            { key: "critical", label: "Critique" },
                                        ].map((p) => (
                                            <tr key={p.key} className="border-b last:border-0">
                                                <td className="px-4 py-2.5 font-medium">{p.label}</td>
                                                <td className="px-4 py-2.5">
                                                    <div className="flex items-center gap-2">
                                                        <Input
                                                            type="number"
                                                            min="1"
                                                            max="720"
                                                            value={slaHours[p.key] ?? ""}
                                                            onChange={(e) => setSlaHours({ ...slaHours, [p.key]: parseInt(e.target.value) || 0 })}
                                                            className="w-20"
                                                            data-testid={`sla-hours-${p.key}`}
                                                        />
                                                        <span className="text-sm text-gray-500">heures</span>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>

                <div className="card">
                    <div className="card-header flex items-center gap-2">
                        <Clock className="w-4 h-4 text-gray-500" />
                        <h2 className="text-sm font-semibold">Clôture automatique</h2>
                    </div>
                    <div className="card-body space-y-4">
                        <p className="text-sm text-gray-500">Les tickets résolus seront automatiquement clôturés après le nombre de jours configuré.</p>
                        <div className="flex items-center gap-3">
                            <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Clôturer après</label>
                            <Input type="number" min="1" max="90" value={autoCloseDays} onChange={(e) => setAutoCloseDays(e.target.value)} className="w-20" data-testid="auto-close-days-input" />
                            <span className="text-sm text-gray-500">jours</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button onClick={handleSave} disabled={saving} size="sm" data-testid="save-settings-btn">
                                {saving ? "..." : "Enregistrer"}
                            </Button>
                            <Button variant="outline" size="sm" onClick={handleAutoClose} disabled={running} data-testid="run-auto-close-btn">
                                <Play className="w-3.5 h-3.5 mr-1" />
                                Exécuter maintenant
                            </Button>
                        </div>
                    </div>
                </div>

                <div className="card">
                    <div className="card-header flex items-center gap-2">
                        <Bell className="w-4 h-4 text-gray-500" />
                        <h2 className="text-sm font-semibold">Relances automatiques</h2>
                    </div>
                    <div className="card-body space-y-4">
                        <p className="text-sm text-gray-500">Les demandeurs reçoivent une relance pour les tickets en attente utilisateur depuis plus de 3 jours.</p>
                        <p className="text-xs text-gray-400">La relance est envoyée automatiquement toutes les 24h. Vous pouvez aussi la déclencher manuellement.</p>
                        <Button variant="outline" size="sm" onClick={handleReminders} disabled={running} data-testid="run-reminders-btn">
                            <Play className="w-3.5 h-3.5 mr-1" />
                            Envoyer les relances maintenant
                        </Button>
                    </div>
                </div>
            </div>

            <div className="mt-6">
                <BackupSection />
            </div>
        </div>
    );
}
